/**
 * Home — Elite-style landing.
 *
 * Full-bleed rotating hero, giant board links (Main / New Faces), agency
 * address block, Latest Work anchor, The Latest strip, Covers grid and an
 * optional Motion reel.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Instagram } from "lucide-react";
import EditorialStrip from "../components/EditorialStrip";
import CoversStrip from "../components/CoversStrip";
import { api } from "../lib/api";
import { usePageMeta } from "../lib/usePageMeta";

const INSTAGRAM_URL = "https://instagram.com/egemodels";

export default function Home() {
  usePageMeta("Ege Models — Model Management · Rotterdam");
  const [models, setModels] = useState([]);

  useEffect(() => {
    api.get("/models").then((r) => setModels(Array.isArray(r.data) ? r.data : [])).catch(() => {});
  }, []);

  const motionModel = models.find((m) => m.featured && m.video_reel) || models.find((m) => m.video_reel);

  const scrollToLatest = () => {
    document.getElementById("latest")?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="home-page">
      <section className="relative h-[100svh] bg-[#F1ECE6] overflow-hidden" data-testid="hero-section">
        <img
          src="/hero/hero-pink.jpg"
          alt="Ege Models editorial"
          data-testid="hero-image"
          className="absolute inset-0 w-full h-full object-cover object-[25%_top] md:object-[center_top]"
        />

        {/* Latest Work — bottom centre anchor */}
        <button
          type="button"
          onClick={scrollToLatest}
          data-testid="hero-latest-link"
          className="absolute bottom-6 left-1/2 -translate-x-1/2 text-black text-[13px] tracking-[0.02em] transition-opacity hover:opacity-60"
        >
          Latest Work
        </button>

        {/* Instagram circle — bottom right */}
        <a
          href={INSTAGRAM_URL}
          target="_blank"
          rel="noreferrer"
          data-testid="hero-instagram-circle"
          aria-label="Ege Models on Instagram"
          className="absolute bottom-6 right-4 sm:right-6 lg:right-8 w-11 h-11 rounded-full bg-black text-white flex items-center justify-center transition-transform hover:scale-105"
        >
          <Instagram size={17}/>
        </a>
      </section>

      <div id="latest">
        <EditorialStrip />
      </div>

      <CoversStrip />

      {/* Motion — featured talent reel */}
      {motionModel && (
        <section className="relative bg-[#0A0A0A] overflow-hidden" data-testid="home-motion">
          <video
            src={motionModel.video_reel}
            autoPlay muted loop playsInline preload="metadata"
            className="w-full h-[70vh] object-cover"
          />
          <div className="absolute inset-x-0 bottom-0 h-40 bg-gradient-to-t from-black/60 to-transparent pointer-events-none"/>
          <div className="absolute bottom-8 left-6 lg:left-12">
            <p className="label-overline text-white/60">Motion</p>
            <Link
              to={`/models/${motionModel.slug}`}
              data-testid="home-motion-link"
              className="font-serif text-2xl md:text-4xl text-white hover:text-white/70 transition-colors"
            >
              {motionModel.name}
            </Link>
          </div>
        </section>
      )}
    </div>
  );
}
